import {
  Input,
  inject,
  OnInit,
  Output,
  Component,
  ViewChild,
  forwardRef,
  DestroyRef,
  ElementRef,
  EventEmitter,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  FormControl,
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { LoggerService } from '@services/logger.service';
import { UserObjectsService } from '../../../core/services/user-objects.service';

import { DataObject } from '../../../core/models/core.models';

import { augmentObjectWithIconAndImage } from '../../utils/object-utils';

import {
  tap,
  map,
  skip,
  take,
  finalize,
  switchMap,
  Observable,
  shareReplay,
  debounceTime,
  distinctUntilChanged,
} from 'rxjs';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { LoadingComponent } from '../loading/loading.component';
import { TranslatePipe } from '@ngx-translate/core';
import { HighlightPipe } from '@pipes/highlight.pipe';

@Component({
  selector: 'app-data-object-select',
  templateUrl: './data-object-select.component.html',
  styleUrls: ['./data-object-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DataObjectSelectComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FaIconComponent,
    FormsModule,
    ReactiveFormsModule,
    LoadingComponent,
    TranslatePipe,
    HighlightPipe,
  ],
})
export class DataObjectSelectComponent implements OnInit, ControlValueAccessor {
  @Input() workspaceId: string | null = null;
  @Input() objectClassIds: number[] | null = null;
  @Input() objectTypeIds: number[] = [];
  @Output() objectSelected = new EventEmitter<DataObject>();
  @ViewChild('inputElement') inputElement: ElementRef;
  @ViewChild('dropdownList') dropdownList: ElementRef;

  searchControl = new FormControl('');
  filteredObjects: DataObject[] = [];
  isOpen = false;
  selectedObject: DataObject | null = null;
  selectedObject$: Observable<DataObject | null>;
  highlightedIndex = -1;
  suggestionsLoading: boolean = false;

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  private destroyRef = inject(DestroyRef);

  constructor(
    private userObjects: UserObjectsService,
    private logger: LoggerService,
  ) {}

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(
        // Skip the very first value which is the initial population
        skip(1),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((query) => this.fetchObjects(query)),
        tap((objects) => {
          this.filteredObjects = objects.map((object) =>
            augmentObjectWithIconAndImage(object),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  fetchObjects(query: string): Observable<DataObject[]> {
    this.suggestionsLoading = true;
    return this.userObjects
      .search$({
        query,
        workspaceId: this.workspaceId,
        objectClassIds: this.objectClassIds,
        objectTypeIds: this.objectTypeIds,
        lean: true,
      })
      .pipe(
        finalize(() => {
          this.suggestionsLoading = false;
        }),
        shareReplay(1), // Cache the result for 1 subscriber or a specific timeframe
      );
  }

  open() {
    this.isOpen = true;
  }

  onInput() {
    this.isOpen = true;
    this.selectedObject = null;
    this.onChange(null);
    this.highlightedIndex = -1;
  }

  onBlur() {
    setTimeout(() => {
      this.isOpen = false;
      this.onTouched();
    }, 200);
  }

  selectObject(object: DataObject) {
    this.selectedObject = object;
    this.searchControl.setValue(object.title, { emitEvent: false });
    this.objectSelected.emit(object);

    // Update the form control or ngModel
    // Trigger the ControlValueAccessor
    this.onChange(object.id);
    this.onTouched();

    this.isOpen = false;
    this.highlightedIndex = -1;
  }

  removeSelection(event: Event) {
    event.stopPropagation();
    this.selectedObject = null;
    this.searchControl.setValue('');
    this.onChange(null);
    this.objectSelected.emit(null);
    setTimeout(() => {
      this.inputElement?.nativeElement.focus();
    }, 0);
  }

  onKeyDown(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightedIndex = Math.min(
          this.highlightedIndex + 1,
          this.filteredObjects.length - 1,
        );
        this.scrollToHighlightedItem();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, -1);
        this.scrollToHighlightedItem();
        break;
      case 'Enter':
        event.preventDefault();
        if (
          this.highlightedIndex >= 0 &&
          this.highlightedIndex < this.filteredObjects.length
        ) {
          this.selectObject(this.filteredObjects[this.highlightedIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.isOpen = false;
        break;
      case 'Tab':
        this.isOpen = false;
        break;
    }
  }

  onFocus() {
    this.logger.debug('onFocus');
    this.isOpen = true;
    // Read the current input value.
    const inputValue: string = this.searchControl.value || '';

    // If the input value is not empty, fetch matching objects.

    this.fetchObjects(inputValue).pipe(
      takeUntilDestroyed(this.destroyRef),
      tap((objects) => {
        // Process the objects by augmenting them with the icon/image.
        this.filteredObjects = objects.map((object) =>
          augmentObjectWithIconAndImage(object),
        );
      }),
    );
  }

  scrollToHighlightedItem() {
    if (this.dropdownList && this.highlightedIndex >= 0) {
      const listElement = this.dropdownList.nativeElement;
      const highlightedElement = listElement.children[
        this.highlightedIndex
      ] as HTMLLIElement;

      if (highlightedElement) {
        const listHeight = listElement.clientHeight;
        const itemHeight = highlightedElement.clientHeight;
        const itemTop = highlightedElement.offsetTop;
        const listScrollTop = listElement.scrollTop;

        if (itemTop < listScrollTop) {
          // Item is above the visible area
          listElement.scrollTop = itemTop;
        } else if (itemTop + itemHeight > listScrollTop + listHeight) {
          // Item is below the visible area
          listElement.scrollTop = itemTop + itemHeight - listHeight;
        }
      }
    }
  }

  // ControlValueAccessor methods
  writeValue(objectId: string | null): void {
    if (!objectId) {
      this.selectedObject = null;
      this.searchControl.setValue('', { emitEvent: false });
      return;
    }

    this.userObjects
      .objectsByIds$([objectId])
      .pipe(
        map((objects: DataObject[]) => objects[0]),
        take(1),
      )
      .subscribe({
        next: (object) => {
          if (object) {
            object = augmentObjectWithIconAndImage(object);
            this.selectedObject = object;
            this.searchControl.setValue(object.title, { emitEvent: false });
          } else {
            console.warn(`No object found for ID: ${objectId}`);
            this.selectedObject = null;
            this.searchControl.setValue('', { emitEvent: false });
          }
        },
        error: (error) => {
          console.error('Error fetching object:', error);
          this.selectedObject = null;
          this.searchControl.setValue('', { emitEvent: false });
        },
      });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.searchControl.disable() : this.searchControl.enable();
  }

  trackById(index: number, object: DataObject): string | number {
    return object.id;
  }
}
